/**
 * dropdownHandlers — reusable event-handler bundle for any `<select>`
 * that lives inside a clickable ancestor (React Router `<Link>`, a card
 * with `onClick={navigate(...)}`, a `role="link"` div, etc.).
 *
 * Why this file exists
 * --------------------
 * A native `<select>` opens its dropdown on `mousedown`, and the click
 * event that follows would normally propagate to any ancestor Link,
 * triggering unintended navigation. A naïve
 *   onClick={e => e.preventDefault()}
 * on the select cancels BOTH the anchor navigation AND the browser's
 * "open the dropdown" default — that was the "requires hover to
 * appear" bug across CDI. The fix must split responsibilities:
 *
 *   • onMouseDown → stopPropagation only (let the native open fire
 *     but keep the mousedown from reaching the Link).
 *   • onClick     → preventDefault + stopPropagation (fires AFTER the
 *     dropdown is already open, so preventDefault only cancels the
 *     anchor's navigation, not the dropdown; stopPropagation prevents
 *     any React handler on the Link from also firing).
 *   • onChange    → stopPropagation, then invoke the caller's update
 *     logic so the selection never bubbles to the Link either.
 *
 * Usage
 * -----
 *   const handlers = linkSafeSelectHandlers((newVal) => moveTo(newVal));
 *   <select {...handlers} value=""> ... </select>
 *
 * The returned object is intentionally spreadable so JSX stays terse
 * and future authors don't have to remember the split-responsibility
 * rationale — they just spread the bundle.
 */

/**
 * @param {(value: string) => (void | Promise<void>)} onValueChange
 *   Business logic to run when the user picks a new option.
 * @returns {{ onMouseDown: Function, onClick: Function, onChange: Function }}
 */
export function linkSafeSelectHandlers(onValueChange) {
  return {
    onMouseDown: (e) => {
      // Let the browser open the native dropdown, but don't let the
      // mousedown reach any ancestor Link / clickable card.
      e.stopPropagation();
    },
    onClick: (e) => {
      // At this point the dropdown is already open (opened on
      // mousedown). preventDefault cancels the anchor's navigation
      // default; stopPropagation stops the click from bubbling to any
      // ancestor Link's synthetic onClick.
      e.preventDefault();
      e.stopPropagation();
    },
    onChange: (e) => {
      e.stopPropagation();
      if (typeof onValueChange === "function") onValueChange(e.target.value);
    },
  };
}
